import React from 'react';

/**
 * QuizPrintView — Static, print-optimized quiz layout.
 * 
 * No animations, no transitions, no hover states, no interactive elements.
 * Receives quiz data as props and renders clean printable markup.
 * Mounted only during print flow to avoid DOM reflow on the main modal.
 */
const QuizPrintView = ({ quiz, showAnswers = false }) => {
    if (!quiz) return null;

    return (
        <div id="quiz-print-container" className="quiz-print-view">
            {/* Header */}
            <div className="quiz-print-header">
                <h1 className="quiz-print-title">{quiz.title || 'Quiz'}</h1>
                <div className="quiz-print-meta">
                    {quiz.grade_level && <span>Grade {quiz.grade_level}</span>}
                    <span>{quiz.questions?.length || 0} Questions</span>
                    <span>{quiz.total_points || 0} Points</span>
                </div>
            </div>

            {/* Student Info */}
            <div className="quiz-print-student-info">
                <div className="quiz-print-field quiz-print-field-name">Name: ______________________</div>
                <div className="quiz-print-field">Date: __________</div>
                <div className="quiz-print-field">Period: ____</div>
            </div>

            {/* Questions */}
            <div className="quiz-print-questions">
                {quiz.questions?.map((q, index) => (
                    <div key={q.id || index} className="quiz-print-question">
                        <div className="quiz-print-question-header">
                            <span className="quiz-print-question-number">{index + 1}</span>
                            <div className="quiz-print-question-body">
                                <div className="quiz-print-question-tags">
                                    <span className="quiz-print-tag">
                                        {q.type === 'mcq' ? 'Multiple Choice' :
                                            q.type === 'true_false' ? 'True / False' :
                                                'Short Answer'}
                                    </span>
                                    <span className="quiz-print-points">{q.points || 10} pts</span>
                                </div>
                                <p className="quiz-print-prompt">{q.prompt}</p>
                            </div>
                        </div>

                        {/* MCQ Choices */}
                        {q.type === 'mcq' && q.choices && (
                            <div className="quiz-print-choices">
                                {q.choices.map((choice, ci) => {
                                    const letter = choice.match(/^([A-D])\)/)?.[1] || String.fromCharCode(65 + ci);
                                    const isCorrect = showAnswers && q.answer?.toUpperCase() === letter.toUpperCase();
                                    return (
                                        <div key={ci} className={`quiz-print-choice ${isCorrect ? 'quiz-print-correct' : ''}`}>
                                            <span className="quiz-print-choice-marker">
                                                {isCorrect ? '✓' : '○'}
                                            </span>
                                            {choice}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* True/False */}
                        {q.type === 'true_false' && (
                            <div className="quiz-print-choices">
                                {['True', 'False'].map(opt => {
                                    const isCorrect = showAnswers && q.answer?.toLowerCase() === opt.toLowerCase();
                                    return (
                                        <div key={opt} className={`quiz-print-choice ${isCorrect ? 'quiz-print-correct' : ''}`}>
                                            <span className="quiz-print-choice-marker">
                                                {isCorrect ? '✓' : '○'}
                                            </span>
                                            {opt}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Short Answer */}
                        {q.type === 'short_answer' && (
                            <div className="quiz-print-short-answer">
                                <div className="quiz-print-answer-lines">
                                    <div className="quiz-print-answer-line" />
                                    <div className="quiz-print-answer-line" />
                                    <div className="quiz-print-answer-line" />
                                </div>
                                {showAnswers && q.answer && (
                                    <div className="quiz-print-answer-key">
                                        <strong>Answer Key:</strong> {q.answer}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default QuizPrintView;
